import type { ReactNode } from "react";
import { useAdmin } from "@/store/AdminContext";

export function PanelCard({
  title,
  description,
  action,
  children,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      {title ? (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-background-200 px-5 py-4">
          <div>
            <h2 className="font-heading text-xl text-foreground-900">{title}</h2>
            {description ? <p className="mt-0.5 text-xs text-foreground-500">{description}</p> : null}
          </div>
          {action}
        </header>
      ) : null}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function ReadOnlyNotice({ text: _text }: { text: string }) {
  const { canEdit } = useAdmin();

  if (canEdit) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center pt-0.5">
          <i className="ri-shield-user-line text-base text-primary-700" />
        </span>
        <p className="text-xs leading-relaxed text-foreground-700">
          Modo administração Vellora — você pode editar este projeto.
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-lg border border-secondary-300 bg-secondary-50 px-4 py-3">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center pt-0.5">
        <i className="ri-eye-line text-base text-secondary-700" />
      </span>
      <p className="text-xs leading-relaxed text-foreground-700">
        Demonstração visual do painel. Os dados são locais; a edição real é configurada no projeto contratado.
      </p>
    </div>
  );
}

export function LockedButton({
  icon = "ri-lock-line",
  children,
  hint,
  onClick,
}: {
  icon?: string;
  children: ReactNode;
  hint?: string;
  onClick?: () => void;
}) {
  const { canEdit } = useAdmin();

  if (canEdit) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={hint ?? "A edição detalhada será ativada na próxima etapa."}
        className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700"
      >
        <i className={`${icon === "ri-lock-line" ? "ri-edit-2-line" : icon} text-sm`} />
        {children}
      </button>
    );
  }

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        disabled
        aria-disabled="true"
        title={hint ?? "Disponível apenas para a administração Vellora"}
        className="inline-flex cursor-not-allowed items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2 text-xs font-medium text-foreground-400"
      >
        <i className={`${icon} text-sm`} />
        {children}
      </button>
      {hint ? <span className="text-[10px] text-foreground-400">{hint}</span> : null}
    </span>
  );
}

export function FieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-background-200 py-2 last:border-0">
      <span className="text-xs text-foreground-500">{label}</span>
      <span className="text-right text-sm text-foreground-800">{value}</span>
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string;
  hint?: string;
  icon?: string;
}) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-[0.16em] text-foreground-500">{label}</span>
        {icon ? (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-primary-600">
            <i className={`${icon} text-base`} />
          </span>
        ) : null}
      </div>
      <p className="mt-2 font-heading text-2xl text-foreground-900">{value}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-foreground-500">{hint}</p> : null}
    </div>
  );
}

export function PanelBadge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "primary" | "accent" | "secondary" }) {
  const tones: Record<string, string> = {
    neutral: "bg-background-200 text-foreground-700",
    primary: "bg-primary-100 text-primary-800",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-100 text-secondary-800",
  };
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] ${tones[tone]}`}>
      {children}
    </span>
  );
}
